export interface Stats {
  maxHp: number;
  maxMp: number;
  attack: number;
  defense: number;
  agility: number;
  /** 0..1 chance to dodge a physical attack. */
  evasion?: number;
}

export const ZERO_STATS: Stats = {
  maxHp: 1,
  maxMp: 0,
  attack: 1,
  defense: 0,
  agility: 1,
  evasion: 0,
};

export function addStats(a: Stats, b: Partial<Stats>): Stats {
  return {
    maxHp: a.maxHp + (b.maxHp ?? 0),
    maxMp: a.maxMp + (b.maxMp ?? 0),
    attack: a.attack + (b.attack ?? 0),
    defense: a.defense + (b.defense ?? 0),
    agility: a.agility + (b.agility ?? 0),
    evasion: (a.evasion ?? 0) + (b.evasion ?? 0),
  };
}

export type StatusEffect = "sleep";

/** Anything that can take a turn in battle. */
export abstract class Combatant {
  abstract readonly name: string;
  hp = 1;
  mp = 0;
  /** Halves incoming physical damage for one round. */
  defending = false;
  statuses = new Map<StatusEffect, number>();

  abstract get stats(): Stats;

  get alive(): boolean {
    return this.hp > 0;
  }

  get maxHp(): number {
    return this.stats.maxHp;
  }

  get maxMp(): number {
    return this.stats.maxMp;
  }

  get hpRatio(): number {
    return this.maxHp > 0 ? this.hp / this.maxHp : 0;
  }

  has(status: StatusEffect): boolean {
    return (this.statuses.get(status) ?? 0) > 0;
  }

  applyStatus(status: StatusEffect, turns: number): void {
    this.statuses.set(status, turns);
  }

  clearStatus(status: StatusEffect): void {
    this.statuses.delete(status);
  }

  /** Returns damage actually dealt. */
  damage(amount: number): number {
    const dealt = Math.max(0, Math.min(amount, this.hp));
    this.hp -= dealt;
    if (this.hp <= 0) {
      this.hp = 0;
      this.statuses.clear();
    }
    return dealt;
  }

  /** Returns HP actually restored. */
  heal(amount: number): number {
    if (!this.alive) return 0;
    const before = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    return this.hp - before;
  }

  spendMp(amount: number): boolean {
    if (this.mp < amount) return false;
    this.mp -= amount;
    return true;
  }

  restoreMp(amount: number): number {
    const before = this.mp;
    this.mp = Math.min(this.maxMp, this.mp + amount);
    return this.mp - before;
  }

  fullHeal(): void {
    this.hp = this.maxHp;
    this.mp = this.maxMp;
    this.statuses.clear();
  }
}
